import { FC } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';
import { Header } from '@/components/shared/Header/Header.tsx';
import { useDomainRecommendations, DomainRecommendationData, SubdomainRecommendation } from '@/api/useDomainRecommendations';
import DownloadIcon from '@/components/icons/DownloadIcon';
import PrintIcon from '@/components/icons/PrintIcon';

const phases = [
  { id: 1, name: 'Discovery', color: '#9F2926', description: 'Organization is an emerging idea and defining its purpose and structure.' },
  { id: 2, name: 'Foundational', color: '#E59836', description: 'Organization operates with governance and minimal structure and systems.' },
  { id: 3, name: 'Emerging', color: '#583B64', description: 'Organization is establishing new gaining clarity in purpose and operations.' },
  { id: 4, name: 'Developing', color: '#76BAA2', description: 'Organization has reliable systems and enabling consistent operations and program delivery.' },
  { id: 5, name: 'Advancing', color: '#3D89A6', description: 'Organization is strategically aligned and learning-focused on sustainability and innovation and impact.' },
  { id: 6, name: 'Thriving', color: '#727272', description: 'Organization is a market sector leader while sustaining adaptive community rooted impact.' }
];

const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    paddingTop: 60,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 15,
    left: 20,
    right: 20,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 90,
    height: 22,
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
  },
  header: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
    color: '#1f2937',
  },
  domainHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
    backgroundColor: '#16A085',
    padding: 15,
    borderRadius: 8,
  },
  domainTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  domainScore: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  definitionSection: {
    backgroundColor: '#16A085',
    padding: 15,
    marginBottom: 15,
    borderRadius: 6,
  },
  definitionLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
  },
  definitionText: {
    fontSize: 12,
    color: '#ffffff',
    lineHeight: 1.4,
  },
  quote: {
    fontSize: 16,
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: 20,
    color: '#374151',
    fontWeight: 'bold',
  },
  tableHeaderContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  tableHeaderCell: {
    flex: 1,
    backgroundColor: '#16A085',
    padding: 12,
    borderRadius: 24,
    maxWidth: 135,
  },
  tableHeaderText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 12,
    alignItems: 'center',
    gap: 10,
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    color: '#374151',
    maxWidth: 135,
  },
  tableCellCenter: {
    textAlign: 'center',
  },
  tableCellBold: {
    fontWeight: 'bold',
  },
  phaseButton: {
    padding: 8,
    borderRadius: 12,
    borderWidth: 2,
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  phaseButtonText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  pathButton: {
    backgroundColor: '#16A085',
    padding: 8,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
  },
  pathButtonText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  phasesSection: {
    marginTop: 30,
  },
  phasesTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 15,
    color: '#1f2937',
  },
  phaseCardsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  phaseCard: {
    width: '48%',
    marginBottom: 10,
    borderRadius: 8,
    borderWidth: 2,
    overflow: 'hidden',
  },
  phaseCardHeader: {
    padding: 12,
    position: 'relative',
  },
  phaseNumber: {
    position: 'absolute',
    top: -15,
    left: -10,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#ffffff',
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  phaseNumberText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  phaseTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginLeft: 15,
  },
  phaseDescription: {
    backgroundColor: '#ffffff',
    padding: 10,
    fontSize: 8,
    lineHeight: 1.3,
    color: '#131313',
    minHeight: 60,
  },
});

// Helper function to get phase button styles for PDF
const getPDFPhaseButtonStyle = (phaseName: string) => {
  const phase = phaseName.toLowerCase();
  
  if (phase.includes('discovery')) {
    return {
      backgroundColor: 'rgba(159, 41, 38, 0.64)',
      borderColor: '#9F2926'
    };
  } else if (phase.includes('foundational')) {
    return {
      backgroundColor: 'rgba(229, 152, 54, 0.64)',
      borderColor: '#E59836'
    };
  } else if (phase.includes('emerging')) {
    return {
      backgroundColor: 'rgba(88, 59, 100, 0.64)',
      borderColor: '#583B64'
    };
  } else if (phase.includes('developing')) {
    return {
      backgroundColor: 'rgba(118, 186, 162, 0.64)',
      borderColor: '#76BAA2'
    };
  } else if (phase.includes('advancing')) {
    return {
      backgroundColor: 'rgba(0, 107, 150, 0.64)',
      borderColor: '#006B96'
    };
  }
  
  return {
    backgroundColor: '#6B7280',
    borderColor: '#6B7280'
  };
};

const DomainRecommendationsPDFDocument: FC<{ domainData: DomainRecommendationData }> = ({ domainData }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.headerBar}>
        <PDFLogo width={90} height={22} />
      </View>
      <Text style={pdfStyles.header}>Domain Recommendations & Action Steps</Text>
      
      <View style={pdfStyles.domainHeader}>
        <Text style={pdfStyles.domainTitle}>Domain: {domainData.name}</Text>
        <Text style={pdfStyles.domainScore}>{domainData.score}%</Text>
      </View>

      <View style={pdfStyles.definitionSection}>
        <Text style={pdfStyles.definitionLabel}>Defined As:</Text>
        <Text style={pdfStyles.definitionText}>
          {domainData.definition}
        </Text>
      </View>

      <Text style={pdfStyles.quote}>{domainData.quote}</Text>

      {/* Table Headers with new styling */}
      <View style={pdfStyles.tableHeaderContainer}>
        <View style={pdfStyles.tableHeaderCell}>
          <Text style={pdfStyles.tableHeaderText}>Sub-domain</Text>
        </View>
        <View style={pdfStyles.tableHeaderCell}>
          <Text style={pdfStyles.tableHeaderText}>Current phase</Text>
        </View>
        <View style={pdfStyles.tableHeaderCell}>
          <Text style={pdfStyles.tableHeaderText}>Goal phase</Text>
        </View>
        <View style={pdfStyles.tableHeaderCell}>
          <Text style={pdfStyles.tableHeaderText}>Your Path</Text>
        </View>
      </View>

      {domainData.subdomains.map((subdomain: SubdomainRecommendation) => (
        <View key={subdomain.id} style={pdfStyles.tableRow}>
          <View style={[pdfStyles.tableCell]}>
            <Text style={[pdfStyles.tableCellBold]}>{subdomain.name}</Text>
          </View>
          <View style={[pdfStyles.tableCell, pdfStyles.tableCellCenter]}>
            <View style={[
              pdfStyles.phaseButton,
              getPDFPhaseButtonStyle(subdomain.current_phase)
            ]}>
              <Text style={pdfStyles.phaseButtonText}>{subdomain.current_phase}</Text>
            </View>
          </View>
          <View style={[pdfStyles.tableCell, pdfStyles.tableCellCenter]}>
            <View style={[
              pdfStyles.phaseButton,
              getPDFPhaseButtonStyle(subdomain.goal_phase)
            ]}>
              <Text style={pdfStyles.phaseButtonText}>{subdomain.goal_phase}</Text>
            </View>
          </View>
          <View style={[pdfStyles.tableCell, pdfStyles.tableCellCenter]}>
            <View style={pdfStyles.pathButton}>
              <Text style={pdfStyles.pathButtonText}>Click here</Text>
            </View>
          </View>
        </View>
      ))}
      <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
    </Page>

    {/* Second page for Development Phases */}
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.headerBar}>
        <PDFLogo width={90} height={22} />
      </View>
      <View style={pdfStyles.phasesSection}>
        <Text style={pdfStyles.phasesTitle}>Development Phases</Text>
        <View style={pdfStyles.phaseCardsContainer}>
          {phases.map((phase) => (
            <View 
              key={phase.id} 
              style={[
                pdfStyles.phaseCard,
                { borderColor: phase.color }
              ]}
            >
              <View style={[
                pdfStyles.phaseCardHeader,
                { backgroundColor: phase.color }
              ]}>
                <View style={[
                  pdfStyles.phaseNumber,
                  { borderColor: phase.color }
                ]}>
                  <Text style={[
                    pdfStyles.phaseNumberText,
                    { color: phase.color }
                  ]}>
                    {phase.id}
                  </Text>
                </View>
                <Text style={pdfStyles.phaseTitle}>
                  {phase.name}
                </Text>
              </View>
              <View style={pdfStyles.phaseDescription}>
                <Text style={{ fontSize: 8, lineHeight: 1.3, color: '#131313' }}>
                  {phase.description}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
      <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
    </Page>
  </Document>
);

const DomainRecommendationsPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assessmentId = searchParams.get('assessmentId');
  const { data: domainData, loading, error } = useDomainRecommendations(id, assessmentId || undefined);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-semibold">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-semibold text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!domainData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-semibold">Domain not found</div>
      </div>
    );
  }

  const getPhaseColor = (phaseName: string) => {
    const phase = phases.find(p => p.name.toLowerCase().includes(phaseName.toLowerCase()));
    return phase?.color || '#6B7280';
  };

  const getPhaseButtonStyle = (phaseName: string) => {
    const baseStyle = {
      padding: '4px 12px',
      width: '160px',
      height: '40px',
      display: 'inline-flex' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      borderRadius: '12px',
      border: '2px solid'
    };

    const phase = phaseName.toLowerCase();
    
    if (phase.includes('discovery')) {
      return {
        ...baseStyle,
        background: 'rgba(159, 41, 38, 0.64)',
        borderColor: '#9F2926'
      };
    } else if (phase.includes('foundational')) {
      return {
        ...baseStyle,
        background: 'rgba(229, 152, 54, 0.64)',
        borderColor: '#E59836'
      };
    } else if (phase.includes('emerging')) {
      return {
        ...baseStyle,
        background: 'rgba(88, 59, 100, 0.64)',
        borderColor: '#583B64'
      };
    } else if (phase.includes('developing')) {
      return {
        ...baseStyle,
        background: 'rgba(118, 186, 162, 0.64)',
        borderColor: '#76BAA2'
      };
    } else if (phase.includes('advancing')) {
      return {
        ...baseStyle,
        background: 'rgba(0, 107, 150, 0.64)',
        borderColor: '#006B96'
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: getPhaseColor(phaseName),
        borderColor: getPhaseColor(phaseName)
      };
    }
  };

  const getNextScore = (currentPhase: string): number => {
    const phase = currentPhase.toLowerCase();
    
    if (phase.includes('discovery')) return 1; 
    if (phase.includes('foundational')) return 2; 
    if (phase.includes('emerging')) return 3; 
    if (phase.includes('developing')) return 4; 
    if (phase.includes('advancing')) return 5; 
    if (phase.includes('thriving')) return 6; 
    
    return 2; 
  };

  const getPhaseTransition = (currentPhase: string, goalPhase: string): string => {
    return `${currentPhase} to ${goalPhase}`;
  };

  const handlePathClick = (subdomain: SubdomainRecommendation) => {
    const nextScore = getNextScore(subdomain.current_phase);
    const phaseTransition = getPhaseTransition(subdomain.current_phase, subdomain.goal_phase);
  
    const url = `/reports/subdomain-recommendations/${subdomain.id}?` +
      `assessmentId=${assessmentId}&` +
      `domainName=${encodeURIComponent(domainData.name)}&` +
      `subdomainName=${encodeURIComponent(subdomain.name)}&` +
      `phaseTransition=${encodeURIComponent(phaseTransition)}&` +
      `score=${nextScore}`;
   
    navigate(url);
  };

  const handlePrint = async (): Promise<void> => {
    if (!domainData) return;
    
    try {
      const pdfDoc = <DomainRecommendationsPDFDocument domainData={domainData} />;
      const asPdf = pdf(pdfDoc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error) {
      console.error('Error generating PDF for print:', error);
    }
  };

  return (
    <div className="">
      <Header 
        title={`Domain: ${domainData.name} (${domainData.score}%)`}
        rightContent={
          <div className="flex flex-row gap-2">
            <PDFDownloadLink
              document={<DomainRecommendationsPDFDocument domainData={domainData} />}
              fileName={`${domainData.name.toLowerCase().replace(/\s+/g, '-')}-recommendations.pdf`}
              className="bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              {({ loading: pdfLoading }: { loading: boolean }) => (
                <>
                  <DownloadIcon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm sm:text-base">
                    {pdfLoading ? 'Generating...' : 'Download'}
                  </span>
                </>
              )}
            </PDFDownloadLink>
            <button
              onClick={handlePrint}
              className="bg-assessment-green text-white px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-colors flex items-center justify-center gap-2"
              type="button"
            >
              <PrintIcon className="w-5 h-5" />
              <span className="hidden sm:inline text-sm sm:text-base">Print PDF</span>
            </button>
          </div>
        }
      />

      <div>
        <div className="bg-assessment-green mt-4 py-4 sm:py-6 px-4 sm:px-8 md:px-[60px] text-white mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
            <span className="font-bold text-xs sm:text-sm whitespace-nowrap">Defined As:</span>
            <span className="text-xs sm:text-sm leading-relaxed">{domainData.definition}</span>
          </div>
        </div>

        <div className="text-center mb-6 sm:mb-8 px-4">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold italic bg-quote-gradient bg-clip-text text-transparent">
            {domainData.quote}
          </h2>
        </div>
        
        <div className="px-2 sm:px-4">
          {/* Mobile: Card view */}
          <div className="block lg:hidden space-y-4">
            {domainData.subdomains.map((subdomain: SubdomainRecommendation) => (
              <div key={subdomain.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="font-semibold text-sm sm:text-base mb-3">{subdomain.name}</div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-medium">Current phase:</span>
                    <span 
                      className="rounded text-white text-xs font-medium px-3 py-1.5"
                      style={{
                        ...getPhaseButtonStyle(subdomain.current_phase),
                        width: 'auto',
                        height: 'auto',
                        minWidth: '100px'
                      }}
                    >
                      {subdomain.current_phase}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 4v12m0 0l-4-4m4 4l4-4" stroke="#131313" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                    </svg>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-medium">Goal phase:</span>
                    <span 
                      className="rounded text-white text-xs font-medium px-3 py-1.5"
                      style={{
                        ...getPhaseButtonStyle(subdomain.goal_phase),
                        width: 'auto',
                        height: 'auto',
                        minWidth: '100px'
                      }}
                    >
                      {subdomain.goal_phase}
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => handlePathClick(subdomain)}
                    className="w-full bg-button-gradient text-white py-2 px-4 rounded-[120px] text-sm font-medium transition-colors hover:opacity-90 mt-2"
                  >
                    View Your Path
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden lg:block rounded-lg overflow-hidden relative">
            <div className="grid grid-cols-4 font-semibold text-sm gap-4 mb-[40px]">
              <div className="text-white text-center py-3 px-6 max-w-[270px] rounded-[24px] bg-domain-gradient mx-auto w-full">
                Sub-domain
              </div>
              <div className="text-white text-center py-3 px-6 max-w-[270px] rounded-[24px] bg-domain-gradient mx-auto w-full">
                Current phase
              </div>
              <div className="text-white text-center py-3 px-6 max-w-[270px] rounded-[24px] bg-domain-gradient mx-auto w-full">
                Goal phase
              </div>
              <div className="text-white text-center py-3 px-6 max-w-[270px] rounded-[24px] bg-domain-gradient mx-auto w-full">
                Your Path
              </div>
            </div>
            
            {domainData.subdomains.map((subdomain: SubdomainRecommendation) => (
              <div key={subdomain.id} className="grid grid-cols-4 border-b border-gray-200 relative">
                <div className="p-4 font-semibold">{subdomain.name}</div>
                <div className="p-4 text-center">
                  <span 
                    className="rounded text-white text-sm font-medium"
                    style={getPhaseButtonStyle(subdomain.current_phase)}
                  >
                    {subdomain.current_phase}
                  </span>
                </div>
                <div className="p-4 text-center">
                  <span 
                    className="rounded text-white text-sm font-medium"
                    style={getPhaseButtonStyle(subdomain.goal_phase)}
                  >
                    {subdomain.goal_phase}
                  </span>
                </div>
                <div className="p-4 text-center">
                  <button 
                    onClick={() => handlePathClick(subdomain)}
                    className="bg-button-gradient text-white py-2 px-3 rounded-[120px] text-sm font-medium transition-colors hover:opacity-90"
                  >
                    Click here
                  </button>
                </div>
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <svg width="81" height="8" viewBox="0 0 81 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M80.3536 4.35355C80.5488 4.15828 80.5488 3.8417 80.3536 3.64644L77.1716 0.464459C76.9763 0.269197 76.6597 0.269197 76.4645 0.464459C76.2692 0.659722 76.2692 0.976304 76.4645 1.17157L79.2929 3.99999L76.4645 6.82842C76.2692 7.02368 76.2692 7.34027 76.4645 7.53553C76.6597 7.73079 76.9763 7.73079 77.1716 7.53553L80.3536 4.35355ZM0 4L4.37114e-08 4.5L80 4.49999L80 3.99999L80 3.49999L-4.37114e-08 3.5L0 4Z" fill="#131313" fillOpacity="0.5"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mt-8 sm:mt-12 md:mt-[60px] px-2 sm:px-0">
          {phases.map((phase) => (
            <div 
              key={phase.id}
              className="rounded-lg shadow-sm border border-gray-100 h-full flex flex-col"
            >
              <div className="mb-4 relative">
                <div 
                  className="w-[46px] h-[46px] rounded-full flex items-center justify-center text-base font-bold absolute top-[-3px] left-[-14px]"
                  style={{ 
                    color: "white",
                    backgroundColor: phase.color,
                    border: `2px solid white`
                  }}
                >
                  {phase.id}
                </div>
              </div>
              <h3 
                className="py-4 sm:py-[20px] px-3 sm:px-4 rounded-tr-lg rounded-tl-lg text-sm sm:text-[14px] whitespace-nowrap text-center text-white font-bold"
                style={{ backgroundColor: phase.color }}
              >
                {phase.name}
              </h3>
              <p 
                style={{ border: `2px solid ${phase.color}` }} 
                className="p-3 sm:p-4 rounded-br-lg rounded-bl-lg flex-1 text-gray-[#131313] bg-white text-xs sm:text-sm leading-relaxed"
              >
                {phase.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DomainRecommendationsPage;